/**
 * Small typed localStorage wrapper for user-side display preferences.
 *
 * Each pref is a free-standing tiny module with a tight API; bigger
 * patterns (zustand/redux) aren't warranted for what is currently a
 * single toggle. Add new prefs by adding (key, default, parse,
 * encode) here and exposing a `usePref()`-style hook from the
 * component that needs them.
 */

const KEY_PRICE_INCLUDES_TAX = "customer-app:prefs:price-includes-tax";

/** True ⇒ price chart + headline add tax + supplier margin to EPEX. */
export function getPriceIncludesTax(): boolean {
  const raw = (typeof localStorage !== "undefined")
    ? localStorage.getItem(KEY_PRICE_INCLUDES_TAX)
    : null;
  // Default ON: customer feedback was "show me what I actually pay"
  // by default; raw EPEX (without margin + tax) is the technical view,
  // not the consumer view. Toggle OFF in /account to see wholesale.
  // raw === null → first-time user → default ON; explicit "0" still OFF.
  return raw !== "0";
}

export function setPriceIncludesTax(v: boolean): void {
  try {
    localStorage.setItem(KEY_PRICE_INCLUDES_TAX, v ? "1" : "0");
    window.dispatchEvent(new Event("prefs:price-includes-tax"));
  } catch {
    /* private-mode Safari throws; the toggle becomes a no-op,
       chart silently stays on the default. */
  }
}

// ----- chart resolution -----------------------------------------------------

const KEY_CHART_RESOLUTION = "customer-app:prefs:chart-resolution";
export type ChartResolution = "1h" | "15min";

/** Bucket size for the Prijzen tab's price chart.
 *
 *  Default "1h": one bar per delivery hour, matches Zonneplan/Frank/GBB
 *  and the customer-app's earnings calc.
 *
 *  "15min": one bar per MTU-15 slot. NL went to 15-min day-ahead in
 *  2025; Tibber bills per quarter and customers used to that view want
 *  to see the price volatility inside the hour. Earnings stays hourly
 *  - this is a chart-display preference, not a billing one.
 */
export function getChartResolution(): ChartResolution {
  const raw = (typeof localStorage !== "undefined")
    ? localStorage.getItem(KEY_CHART_RESOLUTION)
    : null;
  return raw === "15min" ? "15min" : "1h";
}

export function setChartResolution(v: ChartResolution): void {
  try {
    localStorage.setItem(KEY_CHART_RESOLUTION, v);
    window.dispatchEvent(new Event("prefs:chart-resolution"));
  } catch {
    /* see comment above - silent no-op on storage errors. */
  }
}
